// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatLayout.tsx
 * @input Uses React, StyleX, theme tokens, useChatStreamScroll, useChatNewMessages
 * @output Exports ChatLayout component and ChatLayoutProps
 * @position Layout shell for full chat interfaces — messages in page flow, composer fixed to bottom
 *
 * Structural layout only — scroll behavior is delegated to hooks.
 * Provides the scroll container ref and content ref, renders the
 * scroll-to-bottom button, frosted glass dock, and message area.
 *
 * Density (compact/balanced/spacious) is controlled via a prop with
 * 'balanced' as the default. No JS measurement or ResizeObserver needed.
 * The container-type on root enables container queries in child components.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Chat/index.ts (exports)
 * - /apps/storybook/stories/ChatLayout.stories.tsx
 * - /packages/cli/templates/blocks/components/ChatLayout/ (block examples)
 */
import { useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { useChatStreamScroll } from "./useChatStreamScroll.js";
import { useChatNewMessages } from "./useChatNewMessages.js";
import { ChatLayoutScrollButton } from "./ChatLayoutScrollButton.js";
import { ChatLayoutContext } from "./ChatContext.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    kVAEAm: "x1n2onr6",
    k9g6sI: "x12h1iku",
    kAzted: "x2lwn1j",
    kUk6DE: "x98rzlu",
    $$css: true
  },
  rootScrollable: {
    kORKVm: "x1odjw0f",
    kXHlph: "x6ikm8r",
    k5wCbM: "x1mqgsch",
    $$css: true
  },
  messageArea: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kUOVxO: "xvueqy4",
    kAzted: "x1us19tq",
    kGO01o: "xjpqqx5",
    kzqmXN: "xh8yej3",
    ks0D6T: "x193iq5w",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  blurLayer: {
    kVAEAm: "x10l6tqk",
    krVfgx: "x1ey2m1c",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    kfzvcC: "x47corl",
    k6WDB: "xvn2z4z",
    keRtuK: "x67t5t6",
    kZKoxP: "xpyat2d",
    kX1K2I: "xw26lqc",
    kAExgp: "x1bv1mxo",
    $$css: true
  },
  dock: {
    kVAEAm: "x1n2onr6",
    kY2c9j: "x1vjfegm",
    kfzvcC: "x67bb7w",
    kg3NbH: "xrrkdod",
    kGO01o: "xvmdzux",
    $$css: true
  },
  dockInner: {
    kUOVxO: "xvueqy4",
    kzqmXN: "xh8yej3",
    ks0D6T: "x193iq5w",
    $$css: true
  },
  messageAreaCompact: {
    ks0D6T: "x193iq5w",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  messageAreaBalanced: {
    ks0D6T: "x193iq5w",
    kg3NbH: "xnjsko4",
    $$css: true
  },
  messageAreaSpacious: {
    ks0D6T: "x1ap80js",
    kg3NbH: "x1pzlopt",
    $$css: true
  },
  blurLayerCompact: {
    kZKoxP: "xwzfr38",
    kX1K2I: "xkm2don",
    kAExgp: "xiy8rlo",
    $$css: true
  },
  blurLayerBalanced: {
    kZKoxP: "xpyat2d",
    kX1K2I: "xw26lqc",
    kAExgp: "x1bv1mxo",
    $$css: true
  },
  blurLayerSpacious: {
    kZKoxP: "x1wkxgih",
    kX1K2I: "xr5yg4x",
    kAExgp: "xo9xo30",
    $$css: true
  },
  dockCompact: {
    kg3NbH: "xf314gf",
    kGO01o: "x1wesfrj",
    $$css: true
  },
  dockBalanced: {
    kg3NbH: "xrrkdod",
    kGO01o: "xvmdzux",
    $$css: true
  },
  dockSpacious: {
    kg3NbH: "x1pzlopt",
    kGO01o: "xvmdzux",
    $$css: true
  },
  dockInnerCompact: {
    ks0D6T: "x193iq5w",
    $$css: true
  },
  dockInnerBalanced: {
    ks0D6T: "x193iq5w",
    $$css: true
  },
  dockInnerSpacious: {
    ks0D6T: "x1ap80js",
    $$css: true
  }
};

// =============================================================================
// Helpers
// =============================================================================

function hasVisibleContent(children) {
  if (children == null || children === false) {
    return false;
  }
  if (Array.isArray(children) && children.length === 0) {
    return false;
  }
  return true;
}

// =============================================================================
// Component
// =============================================================================

export function ChatLayout({
  children,
  composer,
  density = 'balanced',
  emptyState,
  scrollButton,
  scrollRef: externalScrollRef,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const rootRef = useRef(null);
  const scrollContainerRef = externalScrollRef ?? rootRef;
  const isSelfScrolling = !externalScrollRef;

  // --- Default scroll behavior ---
  const scroll = useChatStreamScroll({
    scrollRef: scrollContainerRef
  });
  const newMsgs = useChatNewMessages({
    isLocked: scroll.isLocked,
    onResize: scroll.scrollIfLocked
  });
  const defaultScrollButton = /*#__PURE__*/_jsx(ChatLayoutScrollButton, {
    isVisible: scroll.isScrolledUp || newMsgs.hasNewMessages,
    label: newMsgs.hasNewMessages ? t('@astryx.chatLayout.newMessages') : undefined,
    onClick: () => {
      newMsgs.dismiss();
      scroll.scrollToBottom();
    }
  });

  // --- Layout context ---
  const layoutContext = useMemo(() => ({
    scrollContainerRef,
    contentRef: newMsgs.contentRef
  }), [scrollContainerRef, newMsgs.contentRef]);

  // --- Derived styles ---
  const showEmpty = !hasVisibleContent(children);
  const densityStyles = {
    compact: {
      messageArea: styles.messageAreaCompact,
      blurLayer: styles.blurLayerCompact,
      dock: styles.dockCompact,
      dockInner: styles.dockInnerCompact
    },
    balanced: {
      messageArea: styles.messageAreaBalanced,
      blurLayer: styles.blurLayerBalanced,
      dock: styles.dockBalanced,
      dockInner: styles.dockInnerBalanced
    },
    spacious: {
      messageArea: styles.messageAreaSpacious,
      blurLayer: styles.blurLayerSpacious,
      dock: styles.dockSpacious,
      dockInner: styles.dockInnerSpacious
    }
  };
  const currentDensity = densityStyles[density];
  return /*#__PURE__*/_jsx(ChatLayoutContext, {
    value: layoutContext,
    children: /*#__PURE__*/_jsxs("div", {
      ...rest,
      ref: mergeRefs(ref, rootRef),
      "data-testid": testId,
      ...mergeProps(themeProps('chat-layout', {
        density
      }), stylex.props(styles.root, isSelfScrolling && styles.rootScrollable, xstyle), className, style),
      children: [/*#__PURE__*/_jsx("div", {
        ...stylex.props(styles.messageArea, currentDensity.messageArea),
        children: showEmpty && emptyState ? /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k x98rzlu xz65tgg"
          },
          children: emptyState
        }) : children
      }), /*#__PURE__*/_jsxs("div", {
        ...{
          0: {
            className: "x1ey2m1c xu96u03 x3m8u43 x1ja2u2z xc8icb0 x47corl xixxii4"
          },
          1: {
            className: "x1ey2m1c xu96u03 x3m8u43 x1ja2u2z xc8icb0 x47corl x7wzq59"
          }
        }[!!isSelfScrolling << 0],
        children: [scrollButton === undefined ? defaultScrollButton : scrollButton, /*#__PURE__*/_jsx("div", {
          ...stylex.props(styles.blurLayer, currentDensity.blurLayer)
        }), /*#__PURE__*/_jsx("div", {
          ...stylex.props(styles.dock, currentDensity.dock),
          children: /*#__PURE__*/_jsx("div", {
            ...stylex.props(styles.dockInner, currentDensity.dockInner),
            children: composer
          })
        })]
      })]
    })
  });
}
ChatLayout.displayName = 'ChatLayout';